import { toast } from '@Shelly/shelly-design-system';
import { MIN_PASSWORD_LENGTH } from 'src/constants';
import { validate_email, validateAllowedSymbols } from 'src/helpers/validators';
import { passwordHasAllMandatorySymbols } from 'src/helpers/utilities';
import type { InputProperties } from 'src/types/InputProperties';
import type { SignUpResponse } from 'src/types/responses/SignUp';

/** i18n formatter injected by Svelte callers via `$format`. */
export type SignUpFormatFunction = (key: string, options?: { default?: string }) => string;

/** Shared initial props for `PasswordUtility` in signup flows. */
export const DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS: InputProperties = {
	size: 'l',
	icon: 'fas fa-lock-keyhole',
	value: '',
	type: 'password',
	label: 'password',
	disabled: false,
	class: 'mb-m width-full',
	autocomplete: 'new-password',
};

/**
 * Validates signup email input.
 * @returns Error message string, or empty string when valid.
 */
export function validateSignUpEmail(email: string, format: SignUpFormatFunction): string {
	if (email === '' || (email && !validate_email(email))) {
		return format('input_error_valid_email', {
			default: 'Please enter a valid email!',
		});
	}

	return '';
}

/**
 * Validates resolved timezone value for registration.
 * @returns Error message when timezone is not resolved, or empty string when valid.
 */
export function validateSignUpTimezone(tz: string | undefined, format: SignUpFormatFunction): string {
	if (tz === undefined) {
		return format('input_error_valid_timezone', {
			default: 'Please select a timezone!',
		});
	}

	return '';
}

/**
 * Validates signup password (allowed symbols, length, mandatory character rules).
 * @returns First matching error message, or empty string when valid.
 */
export function validateSignUpPassword(password: string, format: SignUpFormatFunction): string {
	const passErrorNotAllowedSymbols = validateAllowedSymbols(password)
		? format('password_validate_allowed_symbols', {
				default:
					'Please use only letters (a – z and A – Z), numbers (0 – 9) and uppercase characters such as !@#$%^&*.()',
			})
		: '';

	if (passErrorNotAllowedSymbols !== '') {
		return passErrorNotAllowedSymbols;
	}

	const passErrorLength =
		password === '' || (password.length >= 1 && password.length < MIN_PASSWORD_LENGTH)
			? format('restrict_account_pass_length', {
					default: `Your password should be at least ${MIN_PASSWORD_LENGTH} symbols`,
				})
			: '';

	if (passErrorLength !== '') {
		return passErrorLength;
	}

	const passErrorRequirements = !passwordHasAllMandatorySymbols(password)
		? format('password_error_requirements', {
				default: "The password doesn't meet the requirements!",
			})
		: '';

	return passErrorRequirements;
}

/**
 * Validates terms and privacy consent checkboxes.
 * Shows an info toast when a required consent is missing.
 * @returns `true` when both consents are checked, otherwise `false`.
 */
export function validateSignUpConsents(
	termsCheck: boolean,
	privacyPolicyCheck: boolean,
	format: SignUpFormatFunction,
): boolean {
	if (!termsCheck) {
		toast.open({
			type: 'info',
			title: format('alert_account_create_terms_check_fail', {
				default:
					'Please read and agree to the Terms and Conditions to continue with account creation.',
			}),
		});

		return false;
	}

	if (!privacyPolicyCheck) {
		toast.open({
			type: 'info',
			title: format('alert_account_create_privacy_check_fail', {
				default: 'Please read agree to the Privacy Policy to continue with account creation.',
			}),
		});

		return false;
	}

	return true;
}

export type SignUpAlertData = {
	heading: string;
	message: string;
};

/** Shows the standard success toast after a successful registration. */
export function showSignUpSuccessToast(format: SignUpFormatFunction): void {
	toast.open({
		type: 'success',
		title: format('register_success'),
		duration: 5000,
	});
}

/**
 * Maps a failed registration API response to alert heading and message.
 */
export function getSignUpRegisterAlert(
	res: SignUpResponse,
	format: SignUpFormatFunction,
): SignUpAlertData {
	return {
		heading: format('error_global', {
			default: 'Something went wrong!',
		}),
		message: res?.errors?.used_email
			? format('error_email_used', {
					default: 'Email address is already registered!',
				})
			: format('err_msg_please_try_again', {
					default: 'Please try again!',
				}),
	};
}
